import type { MeStore, StoreTicketLinkKind, StoreTicketType } from "@khazzin/api-client";
import { levelOf, reaches, type SectionKey } from "../../app/shell/sections";

/**
 * The portal's half of the ticket rules (EP10-S12), mirroring
 * `services/api/ticket`: which types each source offers, and which section a
 * source belongs to. The server decides; this only keeps the drawer from
 * offering what it would refuse.
 */

/** Where a ticket is opened from; `none` is a general question. */
export type TicketSource = StoreTicketLinkKind | "none";

export const TYPES_BY_SOURCE: Readonly<Record<TicketSource, readonly StoreTicketType[]>> = {
	none: ["question"],
	order: [
		"question",
		"missing_item",
		"damaged",
		"lost_parcel_claim",
		"address_change_request",
		"cancellation_request",
	],
	shipment: ["question", "missing_item", "damaged", "lost_parcel_claim", "address_change_request"],
	return: ["question", "missing_item", "damaged"],
	statement_line: ["question", "billing_dispute"],
};

const SECTION_BY_SOURCE: Readonly<Record<StoreTicketLinkKind, SectionKey>> = {
	order: "orders",
	shipment: "orders",
	return: "returns",
	statement_line: "finance",
};

/** A claim says how much it is for. */
export function isClaim(type: StoreTicketType): boolean {
	return type === "missing_item" || type === "damaged" || type === "lost_parcel_claim";
}

/**
 * What this account may open from a source: nothing when the source's
 * section is hidden, and a billing dispute only with Finance at write.
 */
export function typesFor(store: MeStore | null, source: TicketSource): StoreTicketType[] {
	if (source !== "none" && levelOf(store, SECTION_BY_SOURCE[source]) === "none") {
		return [];
	}
	return TYPES_BY_SOURCE[source].filter(
		(type) => type !== "billing_dispute" || reaches(levelOf(store, "finance"), "write"),
	);
}

/** The store's ticket reads, which a new ticket or a reply makes stale. */
export function ticketsKey(storeId: string) {
	return ["portal", storeId, "tickets"] as const;
}

/** The portal route of what a ticket is linked to, when the portal has one. */
export function linkedPath(kind: StoreTicketLinkKind, id: string): string | null {
	switch (kind) {
		case "order":
			return `/orders/${encodeURIComponent(id)}`;
		case "return":
			// EP10-S14: the Returns page opens the case's drawer.
			return `/returns?case=${encodeURIComponent(id)}`;
		default:
			return null;
	}
}
